import React from 'react';
import {
  Box,
  AppBar,
  Toolbar,
  Typography,
  IconButton,
  Menu,
  MenuItem,
  Drawer,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  ListItemButton,
  Divider,
  Avatar,
  Button,
  useTheme
} from '@mui/material';
import {
  Menu as MenuIcon,
  Dashboard as DashboardIcon,
  Description as QuotationIcon,
  Work as WorkOrderIcon,
  Receipt as InvoiceIcon,
  AccountBalanceWallet as WalletIcon,
  Payment as PaymentIcon,
  ReceiptLong as StatementIcon,
  Logout as LogoutIcon,
  LockReset as PasswordIcon,
  Notifications as NotificationsIcon,
  EventBusy as DocumentExpiryIcon,
  Checklist as TaskIcon,
  Tune as ServiceSettingsIcon,
  CreditCard as CreditCardIcon,
  Badge as EmployeesIcon,
  BusinessCenter as CompanyDocumentsIcon,
  Block as BlockIcon
} from '@mui/icons-material';
import { useNavigate, useLocation } from 'react-router-dom';
import { ThemeToggle } from '../../components/ui/theme-toggle';
import PortalChangePasswordDialog from './PortalChangePasswordDialog';
import { portalFeaturesForSession } from '../../services/portal/portalFeatures.service';
import { getBrandColor, getBrandInk, getBrandTint, getContrastText, withAlpha } from '../../utils/colorUtils';

const drawerWidth = 240;

// Same order as the staff sidebar (15-admin/NAVIGATION-ORDER.md): work first, then billing, then settings.
// Items with a `feature` show only when the typing center has that portal feature on for this client
// (14-portal/PORTAL-EMPLOYEES-AND-FEATURES.md §3).
const menuSectionsFor = (features) => [
  {
    items: [{ text: 'Dashboard', icon: <DashboardIcon />, path: '/portal/dashboard' }],
  },
  {
    title: 'WORK',
    items: [
      { text: 'Quotations', icon: <QuotationIcon />, path: '/portal/quotations' },
      { text: 'Work Orders', icon: <WorkOrderIcon />, path: '/portal/work-orders' },
      { text: 'Tasks', icon: <TaskIcon />, path: '/portal/tasks', feature: 'TASKS' },
      { text: 'Employees', icon: <EmployeesIcon />, path: '/portal/employees', feature: 'EMPLOYEES' },
      { text: 'Company Documents', icon: <CompanyDocumentsIcon />, path: '/portal/company-documents', feature: 'EMPLOYEES' },
      { text: 'Document Expiry', icon: <DocumentExpiryIcon />, path: '/portal/document-expiry', feature: 'DOC_EXPIRY' },
    ],
  },
  {
    title: 'BILLING',
    items: [
      { text: 'Invoices', icon: <InvoiceIcon />, path: '/portal/invoices' },
      { text: 'Payments', icon: <PaymentIcon />, path: '/portal/payments', feature: 'PAYMENTS' },
      { text: 'Statement', icon: <StatementIcon />, path: '/portal/statements' },
      { text: 'Wallet', icon: <WalletIcon />, path: '/portal/wallet', feature: 'PAYMENTS' },
      { text: 'Saved Cards', icon: <CreditCardIcon />, path: '/portal/cards', feature: 'SAVED_CARDS' },
    ],
  },
  {
    title: 'SETTINGS',
    items: [{ text: 'Service Settings', icon: <ServiceSettingsIcon />, path: '/portal/service-settings' }],
  },
]
  .map((section) => ({ ...section, items: section.items.filter((item) => !item.feature || features[item.feature]) }))
  .filter((section) => section.items.length > 0);

// Pages that belong to a switchable feature — opened directly (bookmark, old link) while the feature
// is off, they show a notice instead. The backend refuses the calls too (task PF6).
const FEATURE_ROUTES = [
  [/^\/portal\/employees/, 'EMPLOYEES'],
  [/^\/portal\/company-documents/, 'EMPLOYEES'],
  [/^\/portal\/document-expiry/, 'DOC_EXPIRY'],
  [/^\/portal\/work-orders\/create/, 'CREATE_WORK_ORDER'],
  [/^\/portal\/tasks/, 'TASKS'],
  [/^\/portal\/(payments|wallet)/, 'PAYMENTS'],
  [/^\/portal\/invoices\/[^/]+\/pay/, 'PAYMENTS'],
  [/^\/portal\/cards/, 'SAVED_CARDS'],
];

// Top-bar title for pages below a menu item (detail, create, pay, top-up).
const SUB_PAGE_TITLES = [
  [/^\/portal\/work-orders\/create/, 'New Work Order'],
  [/^\/portal\/work-orders\/[^/]+/, 'Work Order'],
  [/^\/portal\/quotations\/[^/]+/, 'Quotation'],
  [/^\/portal\/invoices\/[^/]+\/pay/, 'Pay Invoice'],
  [/^\/portal\/invoices\/[^/]+/, 'Invoice'],
  [/^\/portal\/tasks\/[^/]+/, 'Task'],
  [/^\/portal\/employees/, 'Employees'],
  [/^\/portal\/wallet\/topup/, 'Top Up Wallet'],
];

const ROLE_LABELS = { ORG_ADMIN: 'Admin', ORG_VIEWER: 'Viewer' };

const PortalLayout = ({ children, portalUser, onPortalLogout }) => {
  const navigate = useNavigate();
  const theme = useTheme();
  const location = useLocation();
  const [anchorEl, setAnchorEl] = React.useState(null);
  const [passwordOpen, setPasswordOpen] = React.useState(false);
  const [mobileOpen, setMobileOpen] = React.useState(false);

  // Sidebar colours follow the tenant brand, like the staff sidebar.
  const sidebarBackground = theme.palette.mode === 'dark' ? '#0d1117' : getBrandColor(theme);
  const sidebarOnColor = getContrastText(sidebarBackground);
  const onSidebar = (opacity) => withAlpha(sidebarOnColor, opacity);
  const brandInk = getBrandInk(theme);

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const handleMenu = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleLogout = () => {
    handleClose();
    localStorage.removeItem('portalToken');
    localStorage.removeItem('portalUser');
    localStorage.removeItem('portalOrganization');
    localStorage.removeItem('portalAuthenticated');
    if (onPortalLogout) {
      onPortalLogout();
    }
    navigate('/portal/login');
  };

  const actingAsPortalGroupUser = localStorage.getItem('actingAsPortalGroupUser') === 'true';

  const handleBackToCustomers = () => {
    handleClose();
    localStorage.removeItem('portalToken');
    localStorage.removeItem('portalUser');
    localStorage.removeItem('portalOrganization');
    localStorage.removeItem('portalAuthenticated');
    localStorage.removeItem('actingAsPortalGroupUser');
    window.location.href = '/portal/customers';
  };

  const features = portalFeaturesForSession(portalUser);
  const menuSections = menuSectionsFor(features);
  const blockedFeature = FEATURE_ROUTES.find(([pattern, key]) => pattern.test(location.pathname) && !features[key]);
  const menuItems = menuSections.flatMap((section) => section.items);

  const isActive = (path) => {
    if (path === '/portal/dashboard') return location.pathname === path;
    return location.pathname.startsWith(path);
  };

  const pageTitle = SUB_PAGE_TITLES.find(([pattern]) => pattern.test(location.pathname))?.[1]
    || menuItems.find((item) => isActive(item.path))?.text
    || 'Portal';

  const userName = portalUser?.fullName || portalUser?.username || 'User';
  const roleLabel = ROLE_LABELS[portalUser?.role];

  const drawer = (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      {/* Header: user, then organization and role */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, px: 2, pt: 2, pb: 1.5, minHeight: 64 }}>
        <Avatar
          sx={{
            width: 44,
            height: 44,
            bgcolor: onSidebar(0.20),
            border: `2px solid ${onSidebar(0.20)}`,
            fontWeight: 600,
            fontSize: '1rem',
            color: sidebarOnColor,
            flexShrink: 0
          }}
        >
          {userName.charAt(0).toUpperCase()}
        </Avatar>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography
            sx={{
              fontWeight: 600,
              color: sidebarOnColor,
              fontSize: '0.9rem',
              lineHeight: 1.3,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap'
            }}
          >
            {userName}
          </Typography>
          <Typography
            sx={{
              color: onSidebar(0.70),
              fontSize: '0.8rem',
              lineHeight: 1.3,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap'
            }}
          >
            {portalUser?.organizationName || 'Organization'}{roleLabel ? ` · ${roleLabel}` : ''}
          </Typography>
        </Box>
      </Box>
      <Divider sx={{ borderColor: onSidebar(0.12) }} />

      {/* Menu Items */}
      <Box sx={{ pt: 1, px: 1, flex: 1, overflowY: 'auto' }}>
        {menuSections.map((section, sectionIndex) => (
          <React.Fragment key={section.title || sectionIndex}>
            {section.title && (
              <Typography
                sx={{
                  fontSize: '0.6875rem',
                  fontWeight: 600,
                  color: onSidebar(0.55),
                  letterSpacing: '0.08em',
                  px: 1.5,
                  pt: 2,
                  pb: 1
                }}
              >
                {section.title}
              </Typography>
            )}
            <List disablePadding>
              {section.items.map((item) => {
                const active = isActive(item.path);
                return (
                  <ListItem key={item.text} disablePadding sx={{ display: 'block', mb: 0.3 }}>
                    <ListItemButton
                      onClick={() => {
                        navigate(item.path);
                        if (mobileOpen) setMobileOpen(false);
                      }}
                      sx={{
                        minHeight: 38,
                        px: 1.5,
                        py: 0.8,
                        borderRadius: '8px',
                        backgroundColor: active ? onSidebar(0.16) : 'transparent',
                        transition: 'all 0.15s ease',
                        '&:hover': {
                          backgroundColor: onSidebar(0.10)
                        }
                      }}
                    >
                      <ListItemIcon
                        sx={{
                          minWidth: 0,
                          mr: 1.5,
                          justifyContent: 'center',
                          color: sidebarOnColor,
                          opacity: 0.9,
                          '& .MuiSvgIcon-root': { fontSize: '1.125rem' }
                        }}
                      >
                        {item.icon}
                      </ListItemIcon>
                      <ListItemText
                        primary={item.text}
                        sx={{
                          '& .MuiListItemText-primary': {
                            color: sidebarOnColor,
                            fontWeight: active ? 600 : 400,
                            fontSize: '0.8125rem'
                          }
                        }}
                      />
                    </ListItemButton>
                  </ListItem>
                );
              })}
            </List>
          </React.Fragment>
        ))}
      </Box>

      {/* Powered by Thaklis */}
      <Box sx={{ p: 1.5, textAlign: 'center', borderTop: `1px solid ${onSidebar(0.12)}` }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.75 }}>
          <img src="/thaklis-logo.png" alt="Thaklis" style={{ width: 20, height: 20, objectFit: 'contain' }} />
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
            <Typography sx={{ fontSize: '0.6rem', color: onSidebar(0.40), lineHeight: 1.2, letterSpacing: '0.05em' }}>
              Powered by
            </Typography>
            <Typography sx={{ fontSize: '0.75rem', fontWeight: 700, color: onSidebar(0.60), lineHeight: 1.2 }}>
              Thaklis
            </Typography>
          </Box>
        </Box>
      </Box>
    </Box>
  );

  const drawerPaperSx = {
    boxSizing: 'border-box',
    width: drawerWidth,
    backgroundColor: sidebarBackground,
    color: sidebarOnColor,
  };

  return (
    <Box sx={{ display: 'flex' }}>
      <AppBar
        position="fixed"
        sx={{
          width: { sm: `calc(100% - ${drawerWidth}px)` },
          ml: { sm: `${drawerWidth}px` },
          bgcolor: 'background.paper',
          color: 'text.primary',
          boxShadow: '0px 2px 4px rgba(0,0,0,0.1)'
        }}
      >
        <Toolbar>
          <IconButton
            edge="start"
            onClick={handleDrawerToggle}
            sx={{ mr: 2, display: { sm: 'none' }, color: brandInk }}
          >
            <MenuIcon />
          </IconButton>
          <Typography
            variant="h6"
            noWrap
            component="div"
            sx={{ flexGrow: 1, fontWeight: 600, fontSize: '1rem', color: brandInk }}
          >
            {pageTitle}
          </Typography>

          <Typography
            noWrap
            sx={{ mr: 2, fontWeight: 600, fontSize: '0.8125rem', color: 'text.secondary', display: { xs: 'none', md: 'block' } }}
          >
            {portalUser?.organizationName || 'Organization'}
          </Typography>

          <ThemeToggle />

          <IconButton sx={{ mr: 1, color: brandInk }}>
            <NotificationsIcon />
          </IconButton>

          <IconButton
            size="large"
            onClick={handleMenu}
            sx={{ color: brandInk }}
          >
            <Avatar sx={{ width: 32, height: 32, bgcolor: 'primary.main', fontSize: '0.875rem' }}>
              {userName.charAt(0).toUpperCase()}
            </Avatar>
          </IconButton>
          <Menu
            anchorEl={anchorEl}
            open={Boolean(anchorEl)}
            onClose={handleClose}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            transformOrigin={{ vertical: 'top', horizontal: 'right' }}
            PaperProps={{ sx: { minWidth: 240, mt: 0.5 } }}
            MenuListProps={{ dense: true, sx: { py: 0.5 } }}
          >
            {/* Who is signed in — plain text, not a (disabled, greyed-out) menu item */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, px: 2, pt: 1, pb: 1.25 }}>
              <Avatar sx={{ width: 32, height: 32, bgcolor: 'primary.main', fontSize: '0.875rem' }}>
                {userName.charAt(0).toUpperCase()}
              </Avatar>
              <Box sx={{ minWidth: 0 }}>
                <Typography sx={{ fontSize: '0.8125rem', fontWeight: 600, color: 'text.primary' }} noWrap>{userName}</Typography>
                <Typography sx={{ fontSize: '0.75rem', color: 'text.secondary' }} noWrap>{portalUser?.email}</Typography>
                <Typography sx={{ fontSize: '0.6875rem', color: 'text.secondary' }} noWrap>
                  {portalUser?.organizationName}{roleLabel ? ` · ${roleLabel}` : ''}
                </Typography>
              </Box>
            </Box>
            <Divider sx={{ my: 0.5 }} />
            <MenuItem onClick={() => { handleClose(); setPasswordOpen(true); }} sx={{ fontSize: '0.8125rem', minHeight: 34 }}>
              <ListItemIcon sx={{ minWidth: 30 }}><PasswordIcon sx={{ fontSize: 18 }} /></ListItemIcon>
              Change password
            </MenuItem>
            <MenuItem onClick={handleLogout} sx={{ fontSize: '0.8125rem', minHeight: 34, color: 'error.main' }}>
              <ListItemIcon sx={{ minWidth: 30, color: 'error.main' }}><LogoutIcon sx={{ fontSize: 18 }} /></ListItemIcon>
              Log out
            </MenuItem>
          </Menu>
          <PortalChangePasswordDialog open={passwordOpen} onClose={() => setPasswordOpen(false)} />
        </Toolbar>
      </AppBar>

      <Box
        component="nav"
        sx={{ width: { sm: drawerWidth }, flexShrink: { sm: 0 } }}
      >
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={handleDrawerToggle}
          ModalProps={{
            keepMounted: true,
          }}
          sx={{
            display: { xs: 'block', sm: 'none' },
            '& .MuiDrawer-paper': drawerPaperSx,
          }}
        >
          {drawer}
        </Drawer>
        <Drawer
          variant="permanent"
          sx={{
            display: { xs: 'none', sm: 'block' },
            '& .MuiDrawer-paper': { ...drawerPaperSx, borderTopRightRadius: 15, borderBottomRightRadius: 15 },
          }}
          open
        >
          {drawer}
        </Drawer>
      </Box>

      <Box
        component="main"
        sx={{
          flexGrow: 1,
          p: 3,
          width: { sm: `calc(100% - ${drawerWidth}px)` },
          minHeight: '100vh',
          bgcolor: 'background.default'
        }}
      >
        <Toolbar />
        {actingAsPortalGroupUser && (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              flexWrap: 'wrap',
              gap: 1,
              mb: 2,
              px: 2,
              py: 1,
              borderRadius: 1,
              backgroundColor: getBrandTint(theme, 0.08),
              border: '1px solid',
              borderColor: brandInk,
            }}
          >
            <Typography sx={{ fontSize: '0.8125rem', fontWeight: 500 }}>
              Acting as {portalUser?.organizationName || 'this customer'}
            </Typography>
            <Button size="small" onClick={handleBackToCustomers} sx={{ textTransform: 'none', fontSize: '0.8125rem' }}>
              ← Back to my customers
            </Button>
          </Box>
        )}
        {blockedFeature ? (
          <Box sx={{ textAlign: 'center', py: 8, color: 'text.secondary' }}>
            <BlockIcon sx={{ fontSize: 40, color: 'text.disabled', mb: 1 }} />
            <Typography sx={{ fontSize: '0.875rem', fontWeight: 600, color: 'text.primary' }}>
              This feature is turned off for your organization
            </Typography>
            <Typography sx={{ fontSize: '0.8125rem', mb: 2 }}>
              Contact your typing center if you need it.
            </Typography>
            <Button size="small" variant="contained" onClick={() => navigate('/portal/dashboard')} sx={{ textTransform: 'none' }}>
              Go to Dashboard
            </Button>
          </Box>
        ) : children}
      </Box>
    </Box>
  );
};

export default PortalLayout;
